import { ArrowRight, PenTool, Factory, Wrench, ShieldCheck } from "lucide-react";
import Seo, { breadcrumbSchema, localBusinessSchema } from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Btn, Overline } from "@/components/Btn";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CtaBand } from "@/components/CtaBand";
import { COMPANY } from "@/data/company";
import { IMAGES } from "@/data/images";

const values = [
  { icon: PenTool, title: "Conception sur mesure", desc: "Chaque enseigne est étudiée pour votre identité et votre façade, jamais sur catalogue." },
  { icon: Factory, title: "Fabrication maîtrisée", desc: "Aluminium, inox, plexiglas et LED travaillés dans notre atelier pour une qualité constante." },
  { icon: Wrench, title: "Pose par nos équipes", desc: "Installation sur nacelle, raccordement aux normes et maintenance assurée." },
  { icon: ShieldCheck, title: "Engagement de proximité", desc: "Un ancrage franco-suisse qui garantit réactivité et suivi dans la durée." },
];

export default function About() {
  const crumbs = [
    { label: "Accueil", to: "/" },
    { label: "À propos", to: "/a-propos/" },
  ];
  return (
    <>
      <Seo
        title="À propos | Enseigniste fabricant France & Suisse – Ronalp Enseigne"
        description="Ronalp Enseigne, enseigniste fabricant basé à Saint-Jean-de-Gonville (Pays de Gex). Conception, fabrication et pose d'enseignes lumineuses et communication visuelle, France & Suisse."
        path="/a-propos/"
        jsonLd={[localBusinessSchema(), breadcrumbSchema(crumbs)]}
      />
      <section className="relative overflow-hidden border-b border-white/10 bg-ink-900">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.craftsmanCutting} alt="Atelier de fabrication d'enseignes" className="h-full w-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/80 to-ink-900/55" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
          <Breadcrumb items={crumbs} />
          <Reveal className="mt-8 max-w-3xl">
            <Overline>À propos</Overline>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white leading-[0.98]">Enseigniste fabricant, de l'idée à la pose</h1>
            <p className="mt-5 text-lg text-slate-300">Ronalp Enseigne est spécialisé dans la conception, la fabrication et l'installation d'enseignes lumineuses, de signalétique et de communication visuelle. Basés à Saint-Jean-de-Gonville, au cœur du Pays de Gex, nous accompagnons les professionnels en France et en Suisse romande.</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <Reveal className="max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Un vrai métier, pas un simple intermédiaire</h2>
          <p className="mt-4 text-slate-300 leading-relaxed">Trop d'enseignes se résument à un fichier transmis à un sous-traitant. Notre approche est différente : nous maîtrisons physiquement la fabrication, ce qui nous permet de garantir la qualité, de tenir les délais et de proposer un conseil technique réaliste. De l'étude graphique au raccordement électrique, un seul interlocuteur suit votre projet.</p>
          <p className="mt-4 text-slate-300 leading-relaxed">Notre implantation frontalière est un atout : nous intervenons avec la même réactivité dans le Pays de Gex, l'Ain et la Haute-Savoie qu'à Genève, Nyon ou Lausanne, en comprenant les codes et exigences des deux côtés de la frontière.</p>
        </Reveal>

        <Stagger className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {values.map((v) => (
            <StaggerItem key={v.title}>
              <div className="flex items-start gap-5 rounded-xl border border-white/10 bg-ink-800 p-7 h-full">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-signature/15 text-signature"><v.icon className="h-6 w-6" /></div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{v.desc}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal className="mt-12">
          <Btn to="/contact/" size="lg">Demander un devis <ArrowRight className="h-4 w-4" /></Btn>
        </Reveal>
      </section>

      <CtaBand />
    </>
  );
}
